/**
 * L'Aide du téléphone (ticket 14 du chantier `telephone`) : les gestes du téléphone, un par
 * écran. Des données, sans rendu : `AideAuPouce.tsx` les montre, `DessinDuGeste.tsx` dessine
 * leur `scene`.
 *
 * Les textes viennent de la maquette validée par Igor le 2026-09-28, revus au ticket 08 du
 * chantier `aide`, puis au ticket 05 du chantier `telephone-cadre` pour le nouveau cadre :
 * l'ouverture sur Maintenant et ses cinq boutons, la tâche en grand, « Autre chose », le bouton
 * Réglages, et un seul geste dans la liste (le rond, le ›, l'appui long, le titre ☰) ; ni Carte,
 * ni Hiérarchie, ni « Hiérarchiser », ni dépliage au téléphone. Puis au ticket 07 du chantier
 * `telephone-journee-agenda`, seize sujets : la journée (la question et ses deux choix,
 * « Propose-moi », « Je choisis », la journée en cours et « Plus tard », « Changer ma journée »)
 * et l'Agenda (la semaine en cartes, le filtre, ⏸ et « Relancer », le mois, le jour ouvert).
 * Puis au ticket 06 du chantier `telephone-projets-fiche`, dix-neuf sujets : Tous les projets
 * (les tuiles, le cercle), un projet (ses étapes d'abord, « Ici, tu peux faire »), la fiche (ses
 * trois lignes de pastilles, le titre qui se renomme) et ses Réglages en trois groupes, le +
 * selon la page et ses sortes, le nouveau projet ; plus de champ en bas d'un projet ni de Carte
 * dans les dessins. `tests/client/aide/aideDuTelephone.test.ts` ne vérifie que la découpe du gras ;
 * les textes, les sujets et leur ordre sont vus à l'écran par
 * `scripts/verification/aide-du-telephone.mjs`. Le gras en `<b>…</b>`, pour ce qu'on touche.
 * Chaque geste est vérifié au doigt (`scripts/verification/gestes-du-telephone.mjs`). Jamais le double-clic ni la molette
 * ici. Le tableau reste un littéral sans type dedans : les scripts de vérification le lisent tel
 * quel.
 */

/** Le dessin qui montre un geste. */
export type SceneDuGeste =
  | 'barre'
  | 'maintenant'
  | 'reglages'
  | 'projets'
  | 'toucher'
  | 'fil'
  | 'long'
  | 'fiche'
  | 'panneau'
  | 'coche'
  | 'ajouter'
  | 'nouveau'
  | 'question'
  | 'propose'
  | 'choisis'
  | 'journee'
  | 'changer'
  | 'agenda'
  | 'mois';

export interface SujetDeLAide {
  /** Le titre du sujet. */
  titre: string;
  /** Ce dont il parle, en deux mots. */
  resume: string;
  /** Le nom du geste. */
  geste: string;
  /** Le texte, un paragraphe par ligne, le gras en `<b>…</b>`. */
  paragraphes: readonly string[];
  scene: SceneDuGeste;
}

export const SUJETS_DE_L_AIDE: readonly SujetDeLAide[] = [
  { titre: 'Se repérer', resume: 'La barre du bas', geste: 'Toucher',
    paragraphes: ['L’app s’ouvre sur <b>Maintenant</b>. En bas, cinq boutons : <b>Maintenant</b>, <b>Projets</b>, le <b>+</b>, <b>Agenda</b>, <b>Journée</b>.', 'Projets pour les ouvrir, Agenda pour voir venir, Journée pour ta journée. Le + ajoute là où tu es.'],
    scene: 'barre' },
  { titre: 'Ce que tu peux faire', resume: 'Une tâche en grand', geste: 'Toucher',
    paragraphes: ['Sur Maintenant, une seule tâche en grand, et pourquoi elle est là. Touche son titre pour ouvrir sa fiche, <b>C’est fait</b> quand elle l’est.', '<b>Autre chose</b> en met une autre en grand. Dessous, le débloquant et <b>Ensuite</b> ; <b>Chercher une autre tâche</b> pour en choisir une dans la liste.'],
    scene: 'maintenant' },
  { titre: 'Régler Maintenant', resume: 'Le bouton Réglages', geste: 'Toucher',
    paragraphes: ['En haut de Maintenant, touche <b>Réglages</b> : Projet, J’ai, Je suis, Courage.', 'Le bouton du bas compte les tâches possibles avant que tu choisisses ; touche-le pour les voir. La croix revient à tout.'],
    scene: 'reglages' },
  { titre: 'Tous les projets', resume: 'Une tuile par projet', geste: 'Toucher',
    paragraphes: ['Sur <b>Projets</b>, une tuile par projet, de sa couleur. En haut à droite, combien de choses y sont possibles ; au milieu, <b>À toi</b> et la prochaine, ou <b>On attend</b> quand le monde doit répondre.', 'Le cercle se remplit avec ce qui est fait, et dit combien sur combien. Les projets qui ont du Prioritaire viennent en premier. Touche une tuile pour ouvrir son projet.'],
    scene: 'projets' },
  { titre: 'Un projet', resume: 'Ses étapes d’abord', geste: 'Toucher',
    paragraphes: ['Dans un projet, d’abord ses étapes et ses tâches. Une ligne qui finit par <b>›</b> est une étape : touche-la pour entrer dedans.', 'Une ligne avec un rond devant est une tâche : touche-la pour ouvrir sa fiche. Ce qui est fait ne se montre plus ; dans une étape, il attend en bas.', 'En haut, touche <b>Ici, tu peux faire</b> pour voir tout ce qui est faisable dedans, jusqu’au fond des étapes : <b>10 min</b>, <b>30 min</b>, <b>1 h</b> ou <b>Tout</b>.', 'Les lignes suivent ton ordre ; il se choisit sur l’ordinateur.'],
    scene: 'toucher' },
  { titre: 'Revenir en arrière', resume: 'Le fil en haut', geste: 'Toucher',
    paragraphes: ['En haut, le fil dit où tu es : <b>Créer une société › La banque</b>.', 'Touche un morceau du fil pour y remonter.'],
    scene: 'fil' },
  { titre: 'Ouvrir la fiche', resume: 'Appui long', geste: 'Garder le doigt posé',
    paragraphes: ['Garde le doigt posé sur une ligne, tâche ou étape : sa fiche s’ouvre, et tu restes où tu es.', 'En haut, le titre avec son <b>☰</b> ouvre la fiche de l’étape où tu es.'],
    scene: 'long' },
  { titre: 'La fiche', resume: 'Ses pastilles', geste: 'Toucher',
    paragraphes: ['Touche son titre pour le renommer. Dessous, trois lignes de pastilles, seulement ce qui est posé : ce qu’elle attend (⏸), ses dates (⚑ échéance, ● rendez-vous, ⧗ délai), ses infos (⌖ adresse, ☎ contact, ↗ lien).', 'Touche une pastille pour la lire en entier. Tes notes prennent le reste ; en bas, <b>C’est fait</b> et <b>J’attends un retour</b>.'],
    scene: 'fiche' },
  { titre: 'Régler une tâche', resume: 'Le bouton Réglages', geste: 'Toucher',
    paragraphes: ['Sur la fiche, touche <b>Réglages</b> : tout le reste, en trois groupes, <b>La tâche</b>, <b>Quand et conditions</b>, <b>Joindre</b>. Une date, une durée en heures et minutes, un contact se posent là.', 'Ce qui est réglé est plein, avec sa croix pour l’enlever ; le reste, en pointillés, se touche pour le régler. En bas, <b>Déplacer</b> et <b>Supprimer</b>.'],
    scene: 'panneau' },
  { titre: 'C’est fait', resume: 'Le rond', geste: 'Toucher',
    paragraphes: ['Touche le rond devant une tâche : elle est faite. Retouche pour annuler.', 'Une étape n’a pas de rond : coche-la dans sa fiche. Dans ta journée, sur <b>Journée</b>, Nextilt te dit aussi ce que ça libère.'],
    scene: 'coche' },
  { titre: 'Ajouter avec le +', resume: 'Là où tu es', geste: 'Toucher',
    paragraphes: ['Le <b>+</b> en bas ajoute là où tu es. Dans un projet ou une étape : une <b>Tâche</b> ou une <b>Idée</b>, dedans. Pour la ranger dans une étape, entre d’abord dedans.', 'Sur Maintenant, une tâche dans le dernier projet où tu as ajouté ; touche son nom pour en choisir un autre. Sur Journée, une tâche de plus dans ta journée.', 'Dans l’Agenda, pour le jour ouvert ou touché : une <b>Tâche</b> dans ta journée de ce jour, un <b>Rendez-vous</b> à son heure, ou une <b>Échéance</b> avant ce jour.'],
    scene: 'ajouter' },
  { titre: 'Un nouveau projet', resume: 'Le + de Projets', geste: 'Écrire',
    paragraphes: ['Sur <b>Projets</b>, le <b>+</b> crée un projet : écris son nom, touche sa couleur, puis <b>Créer</b>. Il s’ouvre aussitôt.', 'Dessous, <b>Partir d’un modèle</b> ou <b>Importer un plan écrit par une IA</b>.'],
    scene: 'nouveau' },
  { titre: 'Planifier ta journée', resume: 'La question', geste: 'Toucher',
    paragraphes: ['Sur <b>Journée</b>, quand rien n’est prévu, Nextilt te pose la question : combien de temps tu as, ton courage, dès quelle heure, où tu seras.', 'Puis deux choix : <b>Propose-moi une journée</b>, ou <b>Je choisis moi-même</b>. Rien ne change avant <b>C’est ma journée</b>.'],
    scene: 'question' },
  { titre: 'Propose-moi une journée', resume: 'Une journée remplie', geste: 'Toucher',
    paragraphes: ['Nextilt remplit ta journée dans ton temps, le débloquant en tête, chaque tâche à son heure. Un rendez-vous garde la sienne.', '<b>×</b> enlève une tâche, <b>↻</b> la remplace par une autre qui tient. <b>+ Ajouter une tâche</b> en ajoute une ; touche une heure pour la fixer.'],
    scene: 'propose' },
  { titre: 'Je choisis moi-même', resume: 'Trois boutons', geste: 'Toucher',
    paragraphes: ['Trois boutons : le projet, le temps, le courage. Chacun ouvre sa liste ; touche ton choix.', 'Dessous, les tâches qui répondent : touche leur <b>+</b>, elles montent dans ta journée, à leur heure. <b>Chercher une autre tâche</b> cherche partout.'],
    scene: 'choisis' },
  { titre: 'Ta journée', resume: 'La tâche de l’heure', geste: 'Toucher',
    paragraphes: ['<b>Journée</b> montre ta journée, chaque tâche à son heure. Celle de l’heure est en grand : <b>C’est fait</b> quand elle l’est.', '<b>Plus tard</b> te laisse choisir : <b>Plus tard aujourd’hui</b>, en fin de journée, ou <b>Pas aujourd’hui</b>, elle sort de ta journée.'],
    scene: 'journee' },
  { titre: 'Changer ta journée', resume: 'Changer ma journée', geste: 'Toucher',
    paragraphes: ['En bas de ta journée, touche <b>Changer ma journée</b> : <b>×</b> enlève une tâche, <b>+ Ajouter une tâche</b> en ajoute, touche une heure pour la changer. Puis <b>C’est ma journée</b>.', '<b>Recommencer</b> repose la question ; ce qui est déjà fait reste.'],
    scene: 'changer' },
  { titre: 'L’Agenda', resume: 'Ta semaine', geste: 'Toucher',
    paragraphes: ['L’Agenda s’ouvre sur ta semaine, chaque chose dans une carte de la couleur de son projet : ● un rendez-vous, ⚑ une échéance, ⏸ une réponse attendue, ◷ ta journée.', '<b>Tous les projets</b>, en haut, n’en garde qu’un ; la croix revient à tout.', 'Une réponse qui n’est pas venue : touche sa carte, puis <b>Relancer</b> ou <b>C’est arrivé</b>.'],
    scene: 'agenda' },
  { titre: 'Un jour de l’Agenda', resume: 'Le mois, un jour', geste: 'Toucher',
    paragraphes: ['<b>Mois</b> montre une barre par chose posée. Touche un jour : il se montre dessous ; <b>Ouvrir</b> le montre en entier.', 'Un jour ouvert dit ce qui est <b>Imposé</b>, ta journée, et <b>Ce qui doit arriver</b>. Là, <b>Planifier ce jour</b> ou <b>Changer ce jour</b>, et <b>Relancer</b> une réponse pas venue.'],
    scene: 'mois' },
];

/** Un morceau de paragraphe : du texte, en gras ou non. */
export interface Morceau {
  texte: string;
  gras: boolean;
}

/** Découpe un paragraphe en morceaux, le gras (`<b>…</b>`) à part : on le rend sans HTML brut. */
export function morceauxDuParagraphe(paragraphe: string): Morceau[] {
  return paragraphe
    .split(/(<b>.*?<\/b>)/)
    .filter((bout) => bout !== '')
    .map((bout) =>
      bout.startsWith('<b>') ? { texte: bout.slice(3, -4), gras: true } : { texte: bout, gras: false }
    );
}

/** Le texte d'un paragraphe tel qu'il se lit à l'écran, sans les marques du gras. */
export function texteLu(paragraphe: string): string {
  return morceauxDuParagraphe(paragraphe)
    .map((m) => m.texte)
    .join('');
}

/**
 * Les mots des petits dessins : des exemples, pas des données de l'utilisateur. Les mots des
 * boutons (la barre du bas, « Autre chose », « Réglages », « C'est ma journée », « Plus tard »,
 * les deux choix de la question, le mois de l'Agenda) viennent de `vocabulaire.ts`.
 */
export const MOTS_DES_DESSINS = {
  raisonDeLaUne: 'Prioritaire · 30 min',
  resumeDesReglages: 'Tous les projets · tout le temps',
  durees: ['10 min', '30 min', '1 h'],
  possibles: 5,
  societe: 'Créer une société',
  statut: 'Le statut',
  banque: 'La banque',
  prendreRendezVous: 'Prendre rendez-vous',
  rassembler: 'Rassembler les pièces',
  rendezVousBanque: 'Rendez-vous banque',
  ficheNotes: 'Mes notes…',
  deposerCapital: 'Déposer le capital',
  demenager: 'Déménager',
  cartons: 'Faire les cartons',
  preavis: 'Envoyer le préavis',
  libere: 'Fait. Ça libère « Rendez-vous banque », « Déposer le capital ».',
  aAjouter: 'Appeler le comptable',
  // Tous les projets : deux tuiles, l'une à toi, l'autre qui attend le monde.
  tuiles: [
    { nom: 'Déménager', couleur: 'dossier-1', possibles: 5, fait: 3, total: 9 },
    { nom: 'Créer une société', couleur: 'dossier-3', possibles: 0, fait: 4, total: 12 },
  ],
  // Un projet : ce que « Ici, tu peux faire » compte.
  ici: 4,
  // La fiche : ce qu'elle attend, ses dates, ses infos, comme les pastilles de l'écran.
  attendu: 'Les statuts',
  dates: ['⚑ Avant le 26 oct.', '● Ven. 9 oct. 14:00'],
  infos: ['⌖ 12 rue Haute', '☎ Mme Martin'],
  // Le panneau Réglages : réglé (plein, la croix) ou en pointillés, par groupe.
  reglagesDeLaFiche: [
    { groupe: 'tache', regle: ['1 h'], vide: ['Difficulté'] },
    { groupe: 'quand', regle: ['⚑ Avant le 26 oct.'], vide: ['Rendez-vous'] },
    { groupe: 'joindre', regle: [], vide: ['Contact', 'Lien web'] },
  ],
  // La journée : les heures d'exemple, la tâche de l'heure, le rendez-vous sur l'ambre.
  temps: '2 h',
  heures: ['9 h', '10 h', '11 h'],
  rendezVous: 'Visite',
  projetChoisi: 'Déménager',
  dureeChoisie: '30 min',
  // L'Agenda : trois jours de la semaine, puis deux semaines du mois et leurs barres.
  agenda: [
    { jour: 'Mar 29', signe: '●', quoi: '10 h · Conseiller création', sorte: 'projet' },
    // Ta journée se dit avec les mots de l'Agenda (`tachesDeLaJournee`).
    { jour: 'Mer 30', signe: '◷', quoi: null, sorte: 'journee' },
    { jour: 'Jeu 1', signe: '⏸', quoi: 'Réponse de la mairie', sorte: 'monde' },
  ],
  mois: [
    { n: 28, barres: [] },
    { n: 29, barres: ['projet'] },
    { n: 30, barres: ['journee'] },
    { n: 1, barres: ['monde', 'projet'] },
    { n: 2, barres: ['projet'] },
    { n: 3, barres: [] },
    { n: 4, barres: [] },
    { n: 5, barres: ['journee'] },
    { n: 6, barres: [] },
    { n: 7, barres: ['projet', 'journee'] },
    { n: 8, barres: [] },
    { n: 9, barres: ['projet'] },
    { n: 10, barres: [] },
    { n: 11, barres: [] },
  ],
  jourTouche: 'Jeudi 1',
} as const;
